import { ExternalLink } from "lucide-react";

export function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === "youtu.be") return `https://www.youtube.com/embed${u.pathname}`;
    if (host.includes("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean)[0]}`;
    if (host.includes("open.spotify.com")) return `https://open.spotify.com/embed${u.pathname}`;
  } catch (e) {}
  return null;
}

export default function BlockRenderer({ blocks = [] }) {
  return (
    <div className="cms-content clearfix">
      {blocks.map((b) => (
        <Block key={b.id} block={b} />
      ))}
      <div style={{ clear: "both" }} />
    </div>
  );
}

function Block({ block }) {
  const d = block.data || {};
  switch (block.type) {
    case "heading": {
      const Tag = `h${d.level || 2}`;
      const size = d.level === 3 ? "text-2xl" : "text-3xl md:text-4xl";
      return <Tag className={`font-heading font-bold text-slate-900 tracking-tight mt-8 mb-3 ${size}`} style={{ clear: "both" }}>{d.text}</Tag>;
    }
    case "paragraph":
      return <div className="text-slate-600 text-lg leading-relaxed mb-4 cms-p" dangerouslySetInnerHTML={{ __html: d.html || "" }} />;
    case "quote":
      return <blockquote className="border-l-4 border-brand pl-5 py-1 my-6 text-xl font-heading text-slate-800 italic" style={{ clear: "both" }}>{d.text}</blockquote>;
    case "divider":
      return <hr className="my-8 border-slate-200" style={{ clear: "both" }} />;
    case "image": {
      const align = d.align || "center";
      const width = d.width || 45;
      if (!d.url) {
        return <div className="my-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 h-40 flex items-center justify-center text-slate-400 text-sm" style={{ clear: "both" }}>Image — add a URL</div>;
      }
      if (align === "full") {
        return <img src={d.url} alt={d.alt} className="w-full rounded-xl my-6" style={{ clear: "both" }} />;
      }
      if (align === "center") {
        return <img src={d.url} alt={d.alt} className="rounded-xl my-6 mx-auto block" style={{ clear: "both", maxWidth: `${width}%` }} />;
      }
      // left / right float with optional wrap
      const floatStyle = d.wrap
        ? { float: align, width: `${width}%`, margin: align === "left" ? "6px 24px 12px 0" : "6px 0 12px 24px" }
        : { display: "block", margin: "16px auto", maxWidth: `${width}%`, clear: "both" };
      return <img src={d.url} alt={d.alt} className="rounded-xl" style={floatStyle} />;
    }
    case "video": {
      const embed = toEmbed(d.url);
      if (!embed) return d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="text-brand underline">{d.url}</a> : null;
      return (
        <div className="my-6 rounded-xl overflow-hidden border border-slate-200" style={{ clear: "both", aspectRatio: "16/9" }}>
          <iframe src={embed} title="video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      );
    }
    case "podcast": {
      const embed = toEmbed(d.url);
      if (embed) {
        return <div className="my-6 rounded-xl overflow-hidden border border-slate-200" style={{ clear: "both" }}>
          <iframe src={embed} title="podcast" className="w-full" height="152" allow="encrypted-media" />
        </div>;
      }
      return <LinkCard d={{ ...d, title: d.title || "Listen to the podcast", description: d.description || d.url }} tag="Podcast" />;
    }
    case "link_card":
      return <LinkCard d={d} />;
    default:
      return null;
  }
}

function LinkCard({ d, tag = "Link" }) {
  return (
    <a href={d.url} target="_blank" rel="noreferrer" className="my-6 flex flex-col sm:flex-row gap-4 border border-slate-200 rounded-xl overflow-hidden hover:border-brand transition-colors no-underline" style={{ clear: "both" }}>
      {d.image && <img src={d.image} alt="" className="sm:w-48 h-40 sm:h-auto object-cover" />}
      <div className="p-5">
        <span className="text-[10px] font-bold uppercase tracking-wide text-brand">{tag}</span>
        <p className="font-heading font-bold text-lg text-slate-900 mt-1">{d.title}</p>
        {d.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{d.description}</p>}
        <span className="text-xs text-slate-400 mt-2 flex items-center gap-1 truncate"><ExternalLink size={12} /> {d.url}</span>
      </div>
    </a>
  );
}
